import type { VoidComponent } from "solid-js";
import { createEffect, createSignal, getOwner, runWithOwner } from "solid-js";
import { TextAndIconButton } from "../../../components/button/Button";
import pMinDelay from "p-min-delay";
import FormVersionService from "../../../services/FormVersionService";
import { InformativeModal, WarningModal } from "../../../components/modal/Modal";
import { useTranslation } from "../../../state/i18n";
import { useSearchParams } from "@solidjs/router";
import { isEmpty } from "lodash-es";
import posthog from "posthog-js";
import { MoreAppError } from "../../../MoreAppError";
import TextLink from "../../../components/link/TextLink";
import SaveNotAllowedModal from "./SaveNotAllowedModal";
import { useFormServices } from "../../../services/formServices";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";
import NavigationService from "../../../services/NavigationService";
import { useQueryClient } from "@tanstack/solid-query";
import { useModalManager } from "../../../components/modal/ModalManager";

const mailtoHref = import.meta.env.VITE_SUPPORT_MAILTO;

type Status = "none" | "validation_error" | "save_not_allowed" | "api_error" | "empty_form" | "publishing";

const MINIMUM_DELAY = 350;

const HeaderPublishButton: VoidComponent = () => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { fieldService, validationService } = useFormServices();
  const [status, setStatus] = createSignal<Status>("none");
  const [errorDetails, setErrorDetails] = createSignal<MoreAppError>();
  const [, setSearchParams] = useSearchParams<{ validation: string }>();
  const queryClient = useQueryClient();

  const publish = async () => {
    setStatus("publishing");

    if (isEmpty(fieldService.getFields())) {
      setStatus("empty_form");
      return;
    }

    if (validationService.hasErrors()) {
      setStatus("validation_error");
      return;
    }

    try {
      await pMinDelay(FormVersionService.publish(queryClient), MINIMUM_DELAY, { delayRejection: false });
      setStatus("none");
      posthog.capture(POSTHOG_EVENTS.DRAFT_PUBLISHED);

      runWithOwner(owner, () => {
        NavigationService.exitFormEditor();
      });
    } catch (e) {
      if (e instanceof MoreAppError) {
        setErrorDetails(e);
        if (e.code === "forms_change_not_allowed") {
          setStatus("save_not_allowed");
          return;
        }
      }
      setStatus("api_error");
    }
  };

  createEffect(() => {
    if (status() === "save_not_allowed") {
      open({
        content: () => (
          <SaveNotAllowedModal
            title={t(($) => $.FORM_EDITOR_PUBLISH_NOT_ALLOWED_TITLE)}
            description={t(($) => $.FORM_EDITOR_PUBLISH_NOT_ALLOWED_DESCRIPTION)}
          />
        ),
        onClose: () => {
          setErrorDetails(undefined);
          setStatus("none");
        },
      });
    } else if (status() === "api_error") {
      open({
        content: () => (
          <WarningModal
            confirmLabel={t(($) => $.FORM_EDITOR_PUBLISH_ERROR_RETRY_BUTTON)}
            cancelLabel={t(($) => $.CANCEL_BUTTON)}
            inlineMessage={errorDetails()?.traceId}
            title={t(($) => $.FORM_EDITOR_PUBLISH_ERROR_TITLE)}
            description={
              <>
                <span>{t(($) => $.FORM_EDITOR_PUBLISH_ERROR_DESCRIPTION)}</span>&nbsp;
                <TextLink label={t(($) => $.SUPPORT_GET_IN_TOUCH_LINK)} href={mailtoHref} />
              </>
            }
            onConfirm={() => {
              void publish();
              setStatus("none");
            }}
          />
        ),
        onClose: () => {
          setErrorDetails(undefined);
          setStatus("none");
        },
      });
    } else if (status() === "validation_error") {
      open({
        content: () => (
          <InformativeModal
            confirmLabel={t(($) => $.FORM_EDITOR_PUBLISH_VALIDATION_WARNING_CONTINUE_BUTTON)}
            closeAriaLabel={t(($) => $.CLOSE_ARIA)}
            title={t(($) => $.FORM_EDITOR_PUBLISH_VALIDATION_WARNING_TITLE)}
            description={t(($) => $.FORM_EDITOR_PUBLISH_VALIDATION_WARNING_DESCRIPTION)}
            onConfirm={() => {
              setSearchParams({ validation: "open" }, { resolve: true });
            }}
          />
        ),
        onClose: () => {
          setStatus("none");
        },
      });
    } else if (status() === "empty_form") {
      open({
        content: () => (
          <InformativeModal
            confirmLabel={t(($) => $.FORM_EDITOR_PUBLISH_EMPTY_FORM_WARNING_CONTINUE_BUTTON)}
            closeAriaLabel={t(($) => $.CLOSE_ARIA)}
            title={t(($) => $.FORM_EDITOR_PUBLISH_EMPTY_FORM_WARNING_TITLE)}
            description={t(($) => $.FORM_EDITOR_PUBLISH_EMPTY_FORM_WARNING_DESCRIPTION)}
          />
        ),
        onClose: () => {
          setStatus("none");
        },
      });
    }
  });

  return (
    <TextAndIconButton
      text={
        status() === "publishing"
          ? t(($) => $.FORM_EDITOR_HEADER_PUBLISH_BUTTON_LOADING)
          : t(($) => $.FORM_EDITOR_HEADER_PUBLISH_BUTTON)
      }
      icon="paperAirplaneRight"
      variant="primary"
      loading={status() === "publishing"}
      onClick={publish}
      data-tour-highlight-id="publish-button"
    />
  );
};

export default HeaderPublishButton;
